// ⚡ Boost sheet — one chip, one sheet, two doors (founder pick 4B + P4,
// round 2). Money leads: pick a new bid (2× prefilled), one green slab
// pays — straight into the Apple Pay sheet when the wallet is armed for
// that exact amount, the classic inline two-step otherwise. Points sit
// beneath as the free door (⭐ redemption v1 rules). Each door only
// renders when it can actually work; the sheet never opens with neither.
//
// Money plumbing stays in FanLiveCard (it owns the Stripe refs): this sheet
// only reports the amount the fan settled on (onQuote → wallet pre-arm) and
// the tap (onMoney). "Other" hands off to the number-pad modal.
//
// Drunk-proof: one bold line, big slabs, "Not now".

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { fanBoostOrder } from "../lib/fanAccount";
import { FanOrderRow, canMoneyBoost, canPointsBoost } from "../lib/fanRequestsModel";
import { formatAmountForCopy, formatSingleAmount } from "../utils/currency";
import { maxBoostFor, roundUpToStep } from "../utils/payinConfig";
import { roundPriceStep } from "../utils/roundPriceStep";
import "./FanBoostSheet.css";
import { t } from "../i18n";

const BOLT = "M13.2 2 5 13.4h5l-1.6 8.6L17 10.4h-5.2Z";

export type FanBoostSheetProps = {
  row: FanOrderRow;
  signedIn: boolean;
  /** Spendable ⭐ balance (signed-in fans). */
  points: number;
  /** ⭐ price — server knob via /me stats (falls back to 300 upstream). */
  boostCost: number;
  /** Amount an Apple Pay sheet is currently armed for (FanLiveCard). */
  walletMarkAmount: number | null;
  /** Debounced valid-amount reports for wallet pre-arming (null = disarm). */
  onQuote: (amount: number | null) => void;
  /** The money tap — FanLiveCard opens the wallet / inline pay for this bid. */
  onMoney: (newAmount: number) => void;
  /** "Other" — a custom amount on the number pad. */
  onOther: () => void;
  onClose: () => void;
  /** Fired with the fresh balance once a points boost really happened (2xx). */
  onPointsBoosted: (newBalance: number) => void;
  /** The server refused a points boost (409): re-read what is true. */
  onRefresh: () => void;
};

/** 2× / 2.5× / 4× the current bid, on the currency's step, under the DJ's cap. */
export function quickBoostAmounts(row: FanOrderRow): number[] {
  const base = roundPriceStep(Number(row.amount || 0), row.currency);
  if (!(base > 0)) return [];
  const cap = maxBoostFor(row.currency, row.djCapsLifted === true);
  const out: number[] = [];
  for (const m of [2, 2.5, 4]) {
    const v = Math.min(roundUpToStep(base * m, row.currency), cap);
    if (v > base && !out.includes(v)) out.push(v);
  }
  return out;
}

const FanBoostSheet: React.FC<FanBoostSheetProps> = ({
  row,
  signedIn,
  points,
  boostCost,
  walletMarkAmount,
  onQuote,
  onMoney,
  onOther,
  onClose,
  onPointsBoosted,
  onRefresh,
}) => {
  const money = canMoneyBoost(row);
  const pts = canPointsBoost(row, { signedIn, points, boostCost });
  const amounts = useMemo(() => quickBoostAmounts(row), [row]);
  const [picked, setPicked] = useState<number | null>(amounts[0] ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-arm the wallet for the prefilled 2× the moment the sheet opens, and
  // again whenever the fan changes their mind. Disarm on close.
  useEffect(() => {
    if (money && picked) onQuote(picked);
    return () => onQuote(null);
    // onQuote is the parent's arm/disarm setter; keyed on the fan's choice
    // only, or every parent render would disarm and re-arm the wallet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked, money]);

  const confirmPoints = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fanBoostOrder(row.orderId);
      if (!res.ok) throw new Error("boost not applied");
      onPointsBoosted(res.points);
    } catch (err: any) {
      // Only a real success marks the request (2026-10-10). A 409 is the
      // server saying no — already boosted, already answered, or not in this
      // account — so it gets the error line like any failure, and the list
      // re-reads the truth. Treated as success, it painted a gold FAN BOOST
      // that never happened (and it stuck on a row outside the account).
      if (err?.response?.status === 409) onRefresh();
      setError(err?.response?.status === 400 ? t("fanBoostSheet.notEnoughPointsYet") : t("fanBoostSheet.couldnTBoostTry"));
      setBusy(false);
    }
  };

  const title = row.searchQuery ? `Boost “${row.searchQuery}”?` : t("fanBoostSheet.boostThisRequest");
  const baseShown = roundPriceStep(Number(row.amount || 0), row.currency);
  const applePayReady = picked !== null && walletMarkAmount === picked;

  // Portaled to <body>: the My Requests tab scrolls inside a position:fixed
  // container, which traps any z-index inside it — rendered in place, the
  // fixed tab bar (z 900) painted over the sheet's bottom edge.
  return createPortal(
    <div
      className="fbs-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="fbs-card" role="dialog" aria-modal="true" aria-label={t("fanBoostSheet.boostYourRequest")}>
        <div className="fbs-grab" aria-hidden="true" />
        <h3 className="fbs-title">{title}</h3>
        <div className="fbs-sub">
          {t("fanBoostSheet.movesItUpThe")}{row.isPlaylist ? t("fanBoostSheet.onlyChargedOnceIt") : t("fanBoostSheet.onlyChargedIfIt")}
        </div>

        {money && (
          <div className="fbs-door fbs-door--money">
            <span className="fbs-badge fbs-badge--green">{t("fanBoostSheet.mostPowerful")}</span>
            <div className="fbs-door-t">{t("fanBoostSheet.addMoney")}</div>
            {amounts.length > 0 ? (
              <>
                <div className="fbs-door-s">
                  {t("fanBoostSheet.nowPickTheNew", { amount: formatSingleAmount(baseShown, row.currency) })}
                </div>
                <div className="fbs-amts">
                  {amounts.map((a) => (
                    <button
                      key={a}
                      type="button"
                      className={`fbs-amt${picked === a ? " on" : ""}`}
                      onClick={() => setPicked(a)}
                    >
                      {formatAmountForCopy(a, row.currency)}
                    </button>
                  ))}
                  <button type="button" className="fbs-amt" onClick={onOther}>
                    {t("fanBoostSheet.other")}
                  </button>
                </div>
                <button
                  type="button"
                  className="fbs-slab fbs-slab--green"
                  disabled={picked === null || busy}
                  onClick={() => picked !== null && onMoney(picked)}
                >
                  <span className="fbs-l1wrap">
                    <svg className="fbs-bolt" width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d={BOLT} />
                    </svg>
                    <span className="fbs-l1">
                      {applePayReady
                        ? t("fanBoostSheet.payWithApplePay")
                        : picked !== null
                        ? t("fanBoostSheet.boostTo", { amount: formatAmountForCopy(picked, row.currency) })
                        : t("fanBoostSheet.boost")}
                    </span>
                  </span>
                  <span className="fbs-l2">
                    {picked !== null
                      ? `${formatAmountForCopy(baseShown, row.currency)} → ${formatAmountForCopy(picked, row.currency)}`
                      : t("fanBoostSheet.pickAnAmount")}
                  </span>
                </button>
              </>
            ) : (
              // A free request has no bid to multiply — the pad names the price.
              <button type="button" className="fbs-slab fbs-slab--green" onClick={onOther}>
                <span className="fbs-l1wrap">
                  <svg className="fbs-bolt" width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d={BOLT} />
                  </svg>
                  <span className="fbs-l1">{t("fanBoostSheet.addATipTo")}</span>
                </span>
                <span className="fbs-l2">{t("fanBoostSheet.chooseAnAmount")}</span>
              </button>
            )}
          </div>
        )}

        {money && pts && <div className="fbs-or">{t("fanBoostSheet.or")}</div>}

        {pts && (
          <div className="fbs-door fbs-door--pts">
            <span className="fbs-badge fbs-badge--gold">{t("fanBoostSheet.free")}</span>
            <button type="button" className="fbs-slab fbs-slab--gold" onClick={confirmPoints} disabled={busy}>
              <span className="fbs-l1">{busy ? t("fanBoostSheet.boosting") : t("fanBoostSheet.usePoints", { boostCost: boostCost.toLocaleString() })}</span>
              <span className="fbs-l2">
                {t("fanBoostSheet.youHaveAfter", { points: points.toLocaleString(), max: Math.max(0, points - boostCost).toLocaleString() })}
              </span>
            </button>
          </div>
        )}

        {error && <div className="fbs-error">{error}</div>}

        <button type="button" className="fbs-skip" onClick={onClose} disabled={busy}>
          {t("fanBoostSheet.notNow")}
        </button>
      </div>
    </div>,
    document.body
  );
};

export default FanBoostSheet;
